// AnimatedBackground.jsx
import React from 'react';
import { ThemeContext } from '../../ThemeContext'; // Adjust path as needed
import StarsBackground from '../StarsBackground/StarsBackground';
import CloudsBackground from '../CloudsBackground/CloudsBackground';

const AnimatedBackground = ({
  children,
  className = '',
  style = {},
  theme,
  opacity = 1,
}) => {
  const { theme: contextTheme } = React.useContext(ThemeContext);

  // Use prop theme if provided, otherwise fall back to context theme
  const currentTheme = theme || contextTheme;

  // Dark theme - enhanced with better wave visibility
  const darkSvgDataUrl = `data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' version='1.1' xmlns:xlink='http://www.w3.org/1999/xlink' xmlns:svgjs='http://svgjs.dev/svgjs' width='1700' height='1080' preserveAspectRatio='none' viewBox='0 0 1700 1080'%3e%3cg mask='url(%26quot%3b%23SvgjsMask1169%26quot%3b)' fill='none'%3e%3crect width='1700' height='1080' x='0' y='0' fill='%230d1929'%3e%3c/rect%3e%3cpath d='M 0%2c15 C 113.4%2c55 340.2%2c197 567%2c215 C 793.8%2c233 907.4%2c96.4 1134%2c105 C 1360.6%2c113.6 1586.8%2c227.4 1700%2c258L1700 1080L0 1080z' fill='rgba(18%2c 35%2c 60%2c 0.5)'%3e%3c/path%3e%3cpath d='M 0%2c477 C 113.4%2c437 340.2%2c285 567%2c277 C 793.8%2c269 907.4%2c436 1134%2c437 C 1360.6%2c438 1586.8%2c313 1700%2c282L1700 1080L0 1080z' fill='rgba(24%2c 48%2c 85%2c 0.4)'%3e%3c/path%3e%3cpath d='M 0%2c743 C 85%2c709.4 255%2c570 425%2c575 C 595%2c580 680%2c751.2 850%2c768 C 1020%2c784.8 1105%2c667.6 1275%2c659 C 1445%2c650.4 1615%2c711.8 1700%2c725L1700 1080L0 1080z' fill='rgba(30%2c 64%2c 120%2c 0.35)'%3e%3c/path%3e%3cpath d='M 0%2c964 C 68%2c951.4 204%2c885.8 340%2c901 C 476%2c916.2 544%2c1044.4 680%2c1040 C 816%2c1035.6 884%2c877 1020%2c879 C 1156%2c881 1224%2c1059 1360%2c1050 C 1496%2c1041 1632%2c877.2 1700%2c834L1700 1080L0 1080z' fill='rgba(34%2c 100%2c 171%2c 0.3)'%3e%3c/path%3e%3c/g%3e%3cdefs%3e%3cmask id='SvgjsMask1169'%3e%3crect width='1700' height='1080' fill='white'%3e%3c/rect%3e%3c/mask%3e%3c/defs%3e%3c/svg%3e`;

  // Light theme - cleaner with subtle green accent
  const lightSvgDataUrl = `data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' version='1.1' xmlns:xlink='http://www.w3.org/1999/xlink' xmlns:svgjs='http://svgjs.dev/svgjs' width='1700' height='1080' preserveAspectRatio='none' viewBox='0 0 1700 1080'%3e%3cg mask='url(%26quot%3b%23SvgjsMask1169%26quot%3b)' fill='none'%3e%3crect width='1700' height='1080' x='0' y='0' fill='%23f6f8fa'%3e%3c/rect%3e%3cpath d='M 0%2c15 C 113.4%2c55 340.2%2c197 567%2c215 C 793.8%2c233 907.4%2c96.4 1134%2c105 C 1360.6%2c113.6 1586.8%2c227.4 1700%2c258L1700 1080L0 1080z' fill='rgba(234%2c 238%2c 242%2c 0.5)'%3e%3c/path%3e%3cpath d='M 0%2c477 C 113.4%2c437 340.2%2c285 567%2c277 C 793.8%2c269 907.4%2c436 1134%2c437 C 1360.6%2c438 1586.8%2c313 1700%2c282L1700 1080L0 1080z' fill='rgba(223%2c 226%2c 229%2c 0.4)'%3e%3c/path%3e%3cpath d='M 0%2c743 C 85%2c709.4 255%2c570 425%2c575 C 595%2c580 680%2c751.2 850%2c768 C 1020%2c784.8 1105%2c667.6 1275%2c659 C 1445%2c650.4 1615%2c711.8 1700%2c725L1700 1080L0 1080z' fill='rgba(46%2c 160%2c 67%2c 0.08)'%3e%3c/path%3e%3cpath d='M 0%2c964 C 68%2c951.4 204%2c885.8 340%2c901 C 476%2c916.2 544%2c1044.4 680%2c1040 C 816%2c1035.6 884%2c877 1020%2c879 C 1156%2c881 1224%2c1059 1360%2c1050 C 1496%2c1041 1632%2c877.2 1700%2c834L1700 1080L0 1080z' fill='rgba(40%2c 167%2c 69%2c 0.06)'%3e%3c/path%3e%3c/g%3e%3cdefs%3e%3cmask id='SvgjsMask1169'%3e%3crect width='1700' height='1080' fill='white'%3e%3c/rect%3e%3c/mask%3e%3c/defs%3e%3c/svg%3e`;

  const backgroundStyle = {
    backgroundImage: `url("${
      currentTheme === 'dark' ? darkSvgDataUrl : lightSvgDataUrl
    }")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    opacity: opacity,
    position: 'relative',
    ...style,
  };

  return (
    <div
      className={`animated-background ${className}`}
      style={backgroundStyle}
      data-theme={currentTheme}
    >
      {/* Stars for dark theme */}
      {currentTheme === 'dark' && <StarsBackground />}

      {/* Clouds for light theme */}
      {currentTheme === 'light' && <CloudsBackground />}

      {/* Content wrapper */}
      <div className="content-wrapper position-relative" style={{ zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
};

export default AnimatedBackground;
